import { useEffect } from "react";

export default function Chatbot() {
  useEffect(() => {
    const modalEl = document.getElementById("exampleModalCenter");
    const chatMessages = document.getElementById("chatMessages");
    const messageInput = document.getElementById("messageInput");
    const sendBtn = document.getElementById("sendBtn");
    const summaryPanel = document.getElementById("summaryPanel");
    const summaryContent = document.getElementById("summaryContent");
    const chatMainContent = document.getElementById("chatMainContent");
    const generateTermSheetBtn = document.getElementById("generateTermSheetBtn");
    const startFullApplicationBtn = document.getElementById("startFullApplicationBtn");

    if (!modalEl || !chatMessages || !messageInput || !sendBtn) return;

    const REQUIRED_KEYS = [
      "property address",
      "purchase price",
      "loan amount",
      "property type",
      "transaction type",
      "close timeline",
      "user credit",
      "occupancy status",
    ];
    let isSending = false;

    const isAllCaptured = (data) =>
      REQUIRED_KEYS.every((k) => (data?.[k] || "").toString().trim() !== "");

    const formatLabel = (key) =>
      key.split(" ").map((x) => x.charAt(0).toUpperCase() + x.slice(1)).join(" ");

    function addUser(text) {
      const row = document.createElement("div");
      row.className = "chat-row user";
      row.innerHTML = `<div class="chat-bubble f-16 text-black">${text}</div>`;
      chatMessages.appendChild(row);
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }

    function addBot(text, helperText, opts) {
      opts = opts || {};
      const formattedText = (text || "").replace(/\n/g, "<br>");
      const formattedHelper = (helperText || "").replace(/\n/g, "<br>");
      const helperStrong = opts.helperStrong === true;
      const helperBlock = helperText
        ? helperStrong
          ? `<span class="text-black f-16 d-block mt-2 w-100"><strong>${formattedHelper}</strong></span>`
          : `<span class="text-grey d-block mt-2 w-100">${formattedHelper}</span>`
        : "";
      const bodyHtml = opts.htmlBody
        ? opts.htmlBody
        : `<div class="bot-message-text w-100"><span>${formattedText}</span>${helperBlock}</div>`;
      const row = document.createElement("div");
      row.className = "chat-row bot";
      row.innerHTML = `
        <div class="chat-bubble d-flex gap-3 align-items-top f-16 text-black">
          <img src="/static/images/AI.svg" alt="">
          <div class="d-flex flex-column w-100 flex-grow-1">
            ${bodyHtml}
          </div>
        </div>`;
      chatMessages.appendChild(row);
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }

    function renderSummary(data) {
      if (!summaryPanel || !summaryContent || !chatMainContent) return;
      if (!isAllCaptured(data)) {
        summaryPanel.classList.remove("show");
        chatMainContent.classList.remove("has-summary");
        chatMainContent.classList.add("no-summary");
        summaryContent.innerHTML = "";
        return;
      }
      summaryContent.innerHTML = "";
      REQUIRED_KEYS.forEach((key) => {
        const item = document.createElement("div");
        item.className = "summary-item f-16 p-3 d-flex justify-content-between";
        item.innerHTML = `<span class="text-black">${formatLabel(key)}</span><span class="text-primary fw-semibold">${data[key] || ""}</span>`;
        summaryContent.appendChild(item);
      });
      summaryPanel.classList.add("show");
      chatMainContent.classList.remove("no-summary");
      chatMainContent.classList.add("has-summary");
    }

    function composeBotMessage(data) {
      if (data.actions && data.main_text && data.main_text.includes("✅ Analysis complete!")) {
        const split = data.main_text.split("✅ Analysis complete!");
        return {
          main: (split[0] || "").trim(),
          helper: `✅ Analysis complete! ${(split[1] || "").trim()}`,
          helperStrong: true,
        };
      }
      const mainText = (data.main_text || "").trim();
      const questionText = (data.question || "").trim();
      const hintFrom = (data.example_hint || "").trim();
      const optionsText =
        hintFrom ||
        (Array.isArray(data.options) && data.options.length ? `e.g. ${data.options.join(", ")}` : "");

      if (questionText) {
        const sep = mainText && !/\s$/.test(mainText) ? " " : "";
        if (mainText && mainText !== questionText) {
          return {
            main: `${mainText}${sep}<strong>${questionText}</strong>`,
            helper: optionsText,
            helperStrong: false,
          };
        }
        return { main: `<strong>${questionText}</strong>`, helper: optionsText, helperStrong: false };
      }
      return { main: mainText, helper: optionsText, helperStrong: false };
    }

    async function sendMessage(overrideMessage) {
      const userMessage = (overrideMessage || messageInput.value || "").trim();
      if (!userMessage || isSending) return;

      isSending = true;
      if (overrideMessage && generateTermSheetBtn) generateTermSheetBtn.disabled = true;
      addUser(userMessage);
      messageInput.value = "";

      try {
        const res = await fetch("/api/crefin-chat/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: userMessage }),
        });
        let data = {};
        try {
          data = await res.json();
        } catch (_e) {
          data = {};
        }

        if (!res.ok) {
          addBot(data.error || "Unable to fetch chatbot response.");
          return;
        }
        if (data.status === "term_sheet_generated") {
          addBot(data.message || "Your term sheet is ready.");
          const openUrl = data.pdf_url
            ? data.pdf_url
            : data.pdf_filename
              ? `/media/term_sheets/${String(data.pdf_filename).split("/").pop()}`
              : "";
          if (openUrl) window.open(openUrl, "_blank");
          return;
        }
        if (data.redirect) {
          window.location.href = data.redirect;
          return;
        }

        const composed = composeBotMessage(data);
        addBot(composed.main, composed.helper, { helperStrong: composed.helperStrong });
        renderSummary(data.captured_data || {});
      } finally {
        isSending = false;
        if (generateTermSheetBtn) generateTermSheetBtn.disabled = false;
      }
    }

    async function resetChat() {
      await fetch("/crefin/clear-session/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      chatMessages.innerHTML = "";
      if (summaryPanel) summaryPanel.classList.remove("show");
      if (chatMainContent) {
        chatMainContent.classList.remove("has-summary");
        chatMainContent.classList.add("no-summary");
      }
      if (summaryContent) summaryContent.innerHTML = "";
      addBot("", "", {
        htmlBody: `
          <div class="bot-message-text w-100">
            <strong class="d-block mb-1">Hello! I'm your AI lending assistant.</strong>
            <span class="d-block">I'll help you structure the perfect loan for your commercial real estate deal. Let's start with the basics - <strong>what are you looking for?</strong></span>
          </div>
        `,
      });
    }

    document.querySelectorAll(".baninput").forEach((el) => {
      el.addEventListener("click", () => {
        const modal = window.bootstrap?.Modal?.getOrCreateInstance(modalEl);
        if (modal) modal.show();
      });
    });

    modalEl.addEventListener("shown.bs.modal", () => {
      resetChat();
      messageInput.focus();
    });

    sendBtn.addEventListener("click", () => sendMessage());
    messageInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    });

    if (generateTermSheetBtn) {
      generateTermSheetBtn.addEventListener("click", () => sendMessage("generate term sheet"));
    }
    if (startFullApplicationBtn) {
      startFullApplicationBtn.addEventListener("click", async () => {
        const res = await fetch("/api/create_temp_user/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: "full loan application" }),
        });
        const data = await res.json();
        if (data.redirect) window.location.href = data.redirect;
      });
    }
  }, []);

  return null;
}
